/**
 * DeletePanel — ADR-123's operator surface, shared by every entity that can be
 * retired: agent, skill, workflow, cron, resource (ADR-132) and one version of a
 * data set (ADR-147) or an agent activities file (ADR-123). See targetLabel().
 *
 * It implements D2's three steps in one component, in order, and will not let
 * the operator skip forward:
 *
 *   1. PLAN     read-only impact. Nothing is written. This is the "verify-zero"
 *               half of the find-all -> verify-zero -> retire directive.
 *   2. CONFIRM  the operator reads the impact. A BLOCKED plan cannot be
 *               confirmed at all — there is no force control anywhere in this
 *               file, because a forceable block is how the unroutable-agent bug
 *               class returns (D5).
 *   3. PROPOSE  one atomic PR through the existing Approve & Deploy path.
 *
 * Two things the UI is careful to say honestly:
 *   - a `fragment` removal EDITS a surviving file (an agent's fleet.yaml profile
 *     block, one cron entry) — calling that a deletion would misdescribe it;
 *   - undo is `git revert`, and it is ASYMMETRIC (D9): files come back,
 *     deregistered crons / revoked grants / retired vault items do not.
 */
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { GateInconclusiveNote } from './GateInconclusiveNote'
import {
  api,
  DeleteAlreadyProposedError,
  DeleteBlockedError,
  type DeleteEdge,
  type DeleteHandOffItem,
  type DeletePlan,
  type DeleteTarget,
  type ProposeSkillResult,
  type ProseRepair,
} from '../api/client'

function em(e: unknown): string {
  return e instanceof Error ? e.message : String(e)
}

/** deps.EdgeRemovalWithheld — an artifact the target owns that this change set
 *  declines to remove, with the reason. It is a flag, not a problem to solve. */
const WITHHELD_KIND = 'removal_withheld'

/**
 * Classes on every expanded state (plan, block, result). The collapsed panel is a
 * one-word button that hosts drop into a toolbar row beside a path or an Edit
 * link, and the expanded panel replaces it in that same row. Without these the
 * row sizes the panel to its longest unbroken sentence — a blocker naming a file
 * path runs past the card and scrolls the whole page sideways. In a flex-wrap
 * row, basis-full takes a line of its own and min-w-0 lets it fit that line;
 * break-words wraps the long paths inside it. In a block parent they are inert.
 */
const EXPANDED = 'basis-full min-w-0 break-words'

/** A human label for the target, matching the backend's Entity.String(). */
function targetLabel(t: DeleteTarget): string {
  // A resource and a data set both have no agent — the resource's path and the
  // set's versioned ref ARE their identities, so the agent/name form would render
  // "resource /resources/x.md" and "dataset /approval-tiers@v1". Mirrors
  // Entity.String() exactly, because PlanSets compares these labels to find the
  // non-target entities.
  if (t.kind === 'resource' || t.kind === 'dataset' || t.kind === 'agent_file') return `${t.kind} ${t.name ?? ''}`
  return t.name ? `${t.kind} ${t.agent}/${t.name}` : `${t.kind} ${t.agent}`
}

/**
 * Belt to the backend's braces: coerce every collection to an array as the plan
 * enters the component.
 *
 * `DeletePlan` declares these non-optional, and the server now honours that
 * (deps.Plan.emptyNotNil). But a nil Go slice marshals to `null`, so for a
 * while it did not — and because the panel reads them with .length/.map, the
 * first access threw and React unmounted the entire SPA to a blank page. One
 * choke point beats a guard at each of the eight read sites: a field added
 * later is covered by construction rather than by remembering.
 */
function withEmptyArrays(p: DeletePlan): DeletePlan {
  return {
    ...p,
    deleting: p.deleting ?? [],
    containment: p.containment ?? [],
    cascade: p.cascade ?? [],
    repair: p.repair ?? [],
    blocked: p.blocked ?? [],
    architect: p.architect ?? [],
    flagged: p.flagged ?? [],
    hand_off: p.hand_off ?? [],
  }
}

export interface DeletePanelProps {
  target: DeleteTarget
  /** Rendered in the trigger button, e.g. "Delete skill". */
  label?: string
  /** Called after a successful propose, so a parent list can refresh. */
  onProposed?: (result: ProposeSkillResult) => void
}

export function DeletePanel({ target, label = 'Delete', onProposed }: DeletePanelProps) {
  const [open, setOpen] = useState(false)
  const [plan, setPlan] = useState<DeletePlan | null>(null)
  const [planning, setPlanning] = useState(false)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const [blocked, setBlocked] = useState<DeleteEdge[] | null>(null)
  const [duplicate, setDuplicate] = useState<DeleteAlreadyProposedError | null>(null)
  const [note, setNote] = useState('')
  const [replacement, setReplacement] = useState('')
  const [repairs, setRepairs] = useState<ProseRepair[] | null>(null)
  const [repairBusy, setRepairBusy] = useState(false)
  const [repairErr, setRepairErr] = useState<string | null>(null)
  const [accepted, setAccepted] = useState<Record<string, boolean>>({})
  const [result, setResult] = useState<ProposeSkillResult | null>(null)

  const reset = () => {
    setOpen(false); setPlan(null); setErr(null); setBlocked(null)
    setNote(''); setReplacement(''); setRepairs(null); setRepairErr(null)
    setAccepted({}); setResult(null)
  }

  const startPlan = async () => {
    setOpen(true); setPlanning(true); setErr(null); setBlocked(null); setPlan(null)
    try {
      setPlan(withEmptyArrays(await api.deletePlan(target)))
    } catch (e) {
      setErr(em(e))
    } finally {
      setPlanning(false)
    }
  }

  const askArchitect = async () => {
    setRepairBusy(true); setRepairErr(null)
    try {
      const resp = await api.deleteProseRepair(target, replacement.trim() || undefined)
      setRepairs(resp.repairs)
      // Pre-accept only the rewrites that PASSED validation and actually changed
      // something. A failed or declined rewrite is never silently included.
      const next: Record<string, boolean> = {}
      for (const r of resp.repairs) {
        if (r.validation.ok && !r.unchanged && r.content) next[r.path] = true
      }
      setAccepted(next)
    } catch (e) {
      setRepairErr(em(e))
    } finally {
      setRepairBusy(false)
    }
  }

  const propose = async () => {
    setBusy(true); setErr(null); setBlocked(null); setDuplicate(null)
    try {
      const proseRepairs: Record<string, string> = {}
      for (const r of repairs ?? []) {
        if (accepted[r.path] && r.content && r.validation.ok && !r.unchanged) {
          proseRepairs[r.path] = r.content
        }
      }
      const res = await api.deletePropose(target, {
        message: note.trim() || undefined,
        proseRepairs: Object.keys(proseRepairs).length ? proseRepairs : undefined,
      })
      setResult(res)
      onProposed?.(res)
    } catch (e) {
      if (e instanceof DeleteBlockedError) {
        setBlocked(e.blocked)
        setErr(e.message)
      } else if (e instanceof DeleteAlreadyProposedError) {
        setDuplicate(e)
      } else {
        setErr(em(e))
      }
    } finally {
      setBusy(false)
    }
  }

  if (!open) {
    return (
      <button type="button" onClick={() => void startPlan()}
        className="text-red-600 hover:text-red-800 underline" data-testid="delete-open">
        {label}
      </button>
    )
  }

  if (result) {
    // THE WARNING REPLACES THE HANDOFF, and the box carries the warning's own colour. Registration
    // failed, so neither thing the success branch offers holds: there is no in-app review handle
    // to deep-link, and the Improvements list is not known to contain this change. A handoff
    // rendered under the warning tells the operator to go and look at a list the sentence above it
    // has just said may not have their change in it.
    if (result.improvement_warning) {
      return (
        <div className={`${EXPANDED} rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 space-y-1`}
          data-testid="delete-result">
          <p>{result.improvement_warning}</p>
          {result.gate === 'inconclusive' && <GateInconclusiveNote />}
        </div>
      )
    }
    return (
      <div className={`${EXPANDED} rounded border border-green-200 bg-green-50 px-3 py-2 text-xs text-green-800 space-y-1`}
        data-testid="delete-result">
        <p>Sent for review. Nothing is removed until you approve it.</p>
        {result.gate === 'inconclusive' && <GateInconclusiveNote className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-amber-800" />}
        {result.improvement_id
          ? <Link to={`/improvements/${encodeURIComponent(result.improvement_id)}/pr`} className="font-medium underline">
              Review &amp; approve in Mission Control →
            </Link>
          : <span>Find it under <Link to="/improvements" className="font-medium underline">Improvements</Link>.</span>}
      </div>
    )
  }

  return (
    <div className={`${EXPANDED} rounded border border-red-200 bg-red-50/50 px-3 py-3 text-xs space-y-3`} data-testid="delete-panel">
      <div className="flex items-start justify-between gap-2">
        {/* min-w-0: a flex item cannot shrink below its longest unbroken word, which
            here is the target's path — without it Cancel is pushed out of the card. */}
        <p className="flex-1 min-w-0 text-gray-800 font-medium">Delete <span className="font-mono">{targetLabel(target)}</span></p>
        <button type="button" onClick={reset} className="shrink-0 text-gray-500 hover:text-gray-700">Cancel</button>
      </div>

      {planning && <p className="text-gray-500" data-testid="delete-planning">Working out the impact…</p>}

      {err && !blocked && <p className="text-red-700 whitespace-pre-wrap break-words" data-testid="delete-error">{err}</p>}

      {duplicate && <AlreadyProposed e={duplicate} />}

      {blocked && <BlockedList edges={blocked} />}

      {plan && !blocked && (
        <>
          {plan.ok ? null : <BlockedList edges={plan.blocked} />}

          <PlanSets plan={plan} />

          {plan.ok && (
            <>
              {plan.architect.length > 0 && (
                <ArchitectSection
                  edges={plan.architect}
                  repairs={repairs}
                  accepted={accepted}
                  busy={repairBusy}
                  err={repairErr}
                  replacement={replacement}
                  onReplacement={setReplacement}
                  onAsk={() => void askArchitect()}
                  onToggle={path => setAccepted(a => ({ ...a, [path]: !a[path] }))}
                />
              )}

              <input type="text" value={note} onChange={e => setNote(e.target.value)}
                placeholder="Optional note for the reviewer"
                className="w-full text-xs border border-gray-300 rounded px-2 py-1" />

              {/* The asymmetry (D9) is real only when the delete leaves external
                  state behind. A resource delete has no hand-off, so claiming
                  its undo is lossy would be scare text, not a warning. */}
              <p className="text-[11px] text-gray-500">
                Undo is <span className="font-mono">git revert</span> on the pull request
                {plan.hand_off.length > 0
                  ? ' — and it is not symmetric: reverting restores the files, but deregistered crons, revoked Console grants and retired vault items do not come back with them.'
                  : ', which restores the files.'}
                {' '}Applying the change also restarts every agent in the fleet.
              </p>

              <div className="flex items-center gap-2">
                <button type="button" disabled={busy} onClick={() => void propose()}
                  className="px-3 py-1 rounded bg-red-600 text-white disabled:opacity-50" data-testid="delete-propose">
                  {busy ? 'Proposing…' : 'Propose deletion'}
                </button>
                <button type="button" onClick={reset} className="text-gray-500 hover:text-gray-700">Cancel</button>
              </div>
            </>
          )}
        </>
      )}
    </div>
  )
}

/**
 * BlockedList renders the hard block. Deliberately offers NO way past it: the
 * operator's path forward is to retire or re-point the referrer first, which is
 * why each row names the exact file and line.
 */
function BlockedList({ edges }: { edges: DeleteEdge[] }) {
  return (
    <div className="rounded border border-red-300 bg-red-100/70 px-3 py-2 space-y-1" data-testid="delete-blocked">
      <p className="font-semibold text-red-800">
        Can't delete this yet — {edges.length} thing{edges.length === 1 ? '' : 's'} still point{edges.length === 1 ? 's' : ''} at it.
      </p>
      <ul className="space-y-1">
        {edges.map((e, i) => (
          <li key={`${e.file}:${e.line}:${i}`} className="text-red-900">
            <span className="font-mono">{e.file}{e.line ? `:${e.line}` : ''}</span> — {e.detail}
          </li>
        ))}
      </ul>
      <p className="text-red-800">
        Retire or re-point those first. There is no override: a reference left dangling would break at run time.
      </p>
    </div>
  )
}

/** PlanSets renders the four informational sets (D4). */
function PlanSets({ plan }: { plan: DeletePlan }) {
  const files = plan.containment.filter(r => !r.fragment)
  const fragments = plan.containment.filter(r => !!r.fragment)
  const alsoDeleted = plan.deleting.filter(d => targetLabel(d) !== targetLabel(plan.target))
  // The SAME partition the pull-request body makes (handler.partitionWithheld),
  // under the SAME heading, because the operator confirming the plan is the one
  // who most needs it and the user guide names "Left in place" as the place the
  // answer appears. A withheld removal is not a thing that needs looking at: it
  // is a file this change set deliberately kept, with the reason. Mixed into
  // "Needs a human look" it reads as an unresolved problem, and the file the
  // guide promised would go is still nowhere in the two lists above.
  const withheld = plan.flagged.filter(e => e.kind === WITHHELD_KIND)
  const flagged = plan.flagged.filter(e => e.kind !== WITHHELD_KIND)

  return (
    <div className="space-y-2" data-testid="delete-plan-sets">
      {alsoDeleted.length > 0 && (
        <Section title={`Also deleted (${alsoDeleted.length})`} testid="delete-cascade">
          <ul>
            {alsoDeleted.map(d => <li key={targetLabel(d)} className="font-mono">{targetLabel(d)}</li>)}
          </ul>
          {plan.cascade.map((e, i) => <p key={i} className="text-gray-600 mt-1">{e.detail}</p>)}
        </Section>
      )}

      {files.length > 0 && (
        <Section title={`Files removed (${files.length})`} testid="delete-files">
          <ul className="max-h-32 overflow-y-auto">
            {files.map(r => <li key={r.path} className="font-mono truncate" title={r.path}>{r.path}</li>)}
          </ul>
        </Section>
      )}

      {/* A fragment EDITS a file that survives — saying "removed" would misdescribe it. */}
      {fragments.length > 0 && (
        <Section title={`Files edited (${fragments.length})`} testid="delete-fragments">
          <ul>
            {fragments.map(r => (
              <li key={`${r.path}:${r.fragment}`}>
                <span className="font-mono">{r.path}</span>
                <span className="text-gray-600"> — removes <span className="font-mono">{r.fragment}</span>; the file stays</span>
              </li>
            ))}
          </ul>
        </Section>
      )}

      {plan.repair.length > 0 && (
        <Section title={`Repairs applied (${plan.repair.length})`} testid="delete-repairs">
          <ul>
            {plan.repair.map((e, i) => (
              <li key={`${e.file}:${i}`}><span className="font-mono">{e.file}</span> — {e.detail}</li>
            ))}
          </ul>
        </Section>
      )}

      {/* The detail already names the path in backticks and carries the reason,
          and a withheld removal is not always line-addressable, so this row is the
          sentence alone — the shape the PR body prints. */}
      {withheld.length > 0 && (
        <Section title={`Left in place — not removed (${withheld.length})`} testid="delete-withheld">
          <ul className="space-y-0.5">
            {withheld.map((e, i) => <li key={`${e.file}:${i}`} className="text-gray-800">{e.detail}</li>)}
          </ul>
        </Section>
      )}

      {flagged.length > 0 && (
        <Section title={`Needs a human look (${flagged.length})`} testid="delete-flagged">
          <ul>
            {flagged.map((e, i) => (
              <li key={`${e.file}:${i}`}>
                <span className="font-mono">{e.file}{e.line ? `:${e.line}` : ''}</span> — {e.detail}
              </li>
            ))}
          </ul>
        </Section>
      )}

      {plan.hand_off.length > 0 && <HandOff items={plan.hand_off} />}
    </div>
  )
}

/**
 * HandOff renders what Mission Control structurally cannot clean (D7). Shared
 * state is called out explicitly because retiring it would break whatever else
 * still uses it — a credential read by two profiles is the canonical case.
 */
function HandOff({ items }: { items: DeleteHandOffItem[] }) {
  return (
    <Section title={`You'll need to finish these outside Mission Control (${items.length})`} testid="delete-handoff">
      <ul className="space-y-0.5">
        {items.map((h, i) => (
          <li key={i} className={h.shared ? 'text-amber-800' : 'text-gray-700'}>
            <span className="uppercase text-[10px] tracking-wider font-semibold text-gray-500">{h.system}</span>{' '}
            {h.detail}
            {h.shared && <span className="font-semibold"> — shared, do not remove</span>}
          </li>
        ))}
      </ul>
    </Section>
  )
}

/**
 * AlreadyProposed is the ADR-123 D6c refusal: this target already has an open
 * delete proposal, so a second one is not opened.
 *
 * It renders the pull request as a LINK rather than a number in a sentence,
 * because the whole refusal is "go and deal with the one that exists" and the
 * operator's next action is to open it. Amber, not red: nothing is broken and
 * nothing was lost — the change they wanted is already proposed.
 *
 * It also names the way forward. Denying the existing proposal in Mission
 * Control closes its pull request and clears its improvement row together, after
 * which this refusal stops firing on its own — the check asks GitHub live rather
 * than remembering a decision. There is deliberately no override here, matching
 * D5: re-proposing is never the remedy for a red delete PR.
 */
function AlreadyProposed({ e }: { e: DeleteAlreadyProposedError }) {
  return (
    <div className="rounded border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 space-y-1"
      data-testid="delete-already-proposed">
      <p className="whitespace-pre-wrap break-words">{e.message}</p>
      {e.prNumber > 0 && e.prUrl && (
        <p>
          <a href={e.prUrl} target="_blank" rel="noreferrer" className="underline font-medium"
            data-testid="delete-already-proposed-link">
            Open pull request #{e.prNumber}
          </a>
          {e.branch && <span className="text-amber-800 font-mono"> · {e.branch}</span>}
        </p>
      )}
      <p className="text-amber-800">
        To start over instead, Deny that proposal in Mission Control — that closes it and clears its
        improvement row, and this delete can then be proposed again.
      </p>
    </div>
  )
}

/**
 * ArchitectSection is the D6 flow. The Architect's rewrites are opt-in per file
 * and only offered when they PASSED validation — a failed rewrite is shown with
 * its errors and cannot be selected, and a declined one is reported as a refusal
 * rather than a failure (ADR-112 D5).
 */
function ArchitectSection(props: {
  edges: DeleteEdge[]
  repairs: ProseRepair[] | null
  accepted: Record<string, boolean>
  busy: boolean
  err: string | null
  replacement: string
  onReplacement: (v: string) => void
  onAsk: () => void
  onToggle: (path: string) => void
}) {
  const { edges, repairs, accepted, busy, err, replacement, onReplacement, onAsk, onToggle } = props
  return (
    <Section title={`Mentioned in prose (${edges.length})`} testid="delete-architect">
      <p className="text-gray-600">
        These name it in free text, so they can't be fixed mechanically. The Fleet Architect can propose rewrites
        that ship in the same pull request — or leave them and edit by hand.
      </p>
      {!repairs && (
        <div className="mt-2 space-y-1">
          <ul className="text-gray-700">
            {edges.map((e, i) => (
              <li key={`${e.file}:${i}`} className="font-mono truncate" title={e.file}>
                {e.file}{e.line ? `:${e.line}` : ''}
              </li>
            ))}
          </ul>
          <input type="text" value={replacement} onChange={e => onReplacement(e.target.value)}
            placeholder="Optional: what takes over this responsibility?"
            className="w-full text-xs border border-gray-300 rounded px-2 py-1" />
          <button type="button" disabled={busy} onClick={onAsk}
            className="px-2 py-1 rounded border border-gray-300 bg-white text-gray-700 disabled:opacity-50"
            data-testid="delete-ask-architect">
            {busy ? 'Asking the Architect…' : 'Ask the Architect to rewrite these'}
          </button>
          {err && <p className="text-amber-700 whitespace-pre-wrap" data-testid="delete-architect-error">{err}</p>}
        </div>
      )}
      {repairs && (
        <ul className="mt-2 space-y-1" data-testid="delete-repair-list">
          {repairs.map(r => {
            const selectable = r.validation.ok && !r.unchanged && !!r.content
            return (
              <li key={r.path} className="flex items-start gap-2">
                <input type="checkbox" disabled={!selectable} checked={!!accepted[r.path]}
                  onChange={() => onToggle(r.path)} className="mt-0.5 shrink-0"
                  data-testid={`delete-repair-${r.path}`} />
                {/* min-w-0 for the same reason as the header: the path would set the row's width. */}
                <span className="flex-1 min-w-0">
                  <span className="font-mono">{r.path}</span>
                  {r.unchanged && <span className="text-gray-600"> — the Architect left this unchanged: {r.rationale || 'no safe rewrite'}</span>}
                  {!r.unchanged && r.validation.ok && r.rationale && <span className="text-gray-600"> — {r.rationale}</span>}
                  {!r.validation.ok && (
                    <span className="text-red-700"> — rejected: {(r.validation.errors ?? []).join('; ')}</span>
                  )}
                </span>
              </li>
            )
          })}
        </ul>
      )}
    </Section>
  )
}

function Section({ title, testid, children }: { title: string; testid?: string; children: React.ReactNode }) {
  return (
    <div data-testid={testid}>
      <p className="text-gray-400 uppercase tracking-wider font-semibold text-[10px]">{title}</p>
      <div className="text-gray-800">{children}</div>
    </div>
  )
}
